import { DirectUpload } from "@rails/activestorage";

// Maximum length of a single recorded clip. Long enough for a sumo bout,
// short enough to keep the blob small enough to upload from a phone.
const MAX_RECORDING_MS = 5 * 60 * 1000;
const RECORD_TIMESLICE_MS = 1000;

// `MediaRecorder` support varies a lot between mobile browsers. Plain
// "video/webm" goes first: it lets the browser match codecs to the actual
// stream (ours usually has no audio track, and naming `opus` up front yields
// zero bytes on some muxers, e.g. Firefox Android). Named variants follow as
// fallbacks. WebM is YouTube's native ingest and processes reliably, while
// MediaRecorder MP4 output (fragmented MP4) is frequently abandoned by
// YouTube processing. MP4 stays last for Safari, which cannot record WebM.
// The recorder walks this list across attempts: an empty capture advances to
// the next candidate, ending with the browser default.
const MIME_CANDIDATES = [
  "video/webm",
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9,opus",
  "video/mp4;codecs=h264,aac",
  "video/mp4",
];

const supportedMimes = () => {
  if (typeof window.MediaRecorder === "undefined") return [];
  return MIME_CANDIDATES.filter((type) => {
    try {
      return window.MediaRecorder.isTypeSupported(type);
    } catch (_e) {
      return false;
    }
  });
};

const extensionFor = (mimeType) => (String(mimeType).includes("mp4") ? "mp4" : "webm");

// `MediaRecorder.mimeType` includes codec parameters (e.g.
// "video/mp4;codecs=vp9"). Active Storage compares the declared content type
// against the bare request content type, so keep only the media type.
const normalizeMimeType = (mimeType) => {
  const bare = String(mimeType || "").split(";")[0].trim();
  return bare || "video/webm";
};

const formatClock = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

// `@rails/activestorage` needs the built-in CSRF meta tag, which the dashboard
// layout renders. Direct upload then hands us back a signed_id.
const directUpload = (file, url, onProgress) =>
  new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, url, {
      directUploadWillStoreFileWithXHR(xhr) {
        xhr.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable || !onProgress) return;
          onProgress(Math.round((event.loaded / event.total) * 100));
        });
      },
    });

    upload.create((error, blob) => {
      if (error) reject(error);
      else resolve(blob.signed_id);
    });
  });

// Registered once per document; Turbo Drive reuses one document across visits.
let activeTeardown = null;

const initClipRecorder = () => {
  const root = document.querySelector("[data-clip-recorder]");
  if (!root || root.dataset.clipRecorderBound === "1") return;
  root.dataset.clipRecorderBound = "1";

  const uploadUrl = root.dataset.uploadUrl;
  const sessionId = root.dataset.sessionId;
  const maxMs = Number(root.dataset.maxMs) || MAX_RECORDING_MS;
  const myWrestlerId = Number(root.dataset.myWrestlerId || 0);
  const opponentPresent = root.dataset.opponentPresent === "true";
  const initialOpponentId = Number(root.dataset.opponentId || 0) || null;
  const initialClipSaved = root.dataset.clipSaved === "true";

  const steps = {
    attest: root.querySelector('[data-clip-step="attest"]'),
    record: root.querySelector('[data-clip-step="record"]'),
    review: root.querySelector('[data-clip-step="review"]'),
  };
  const attestationBox = root.querySelector("[data-clip-attestation]");
  const startBtn = root.querySelector("[data-clip-start]");
  const preview = root.querySelector("[data-clip-preview]");
  const timerEl = root.querySelector("[data-clip-timer]");
  const badge = root.querySelector("[data-clip-recording-badge]");
  const flipBtn = root.querySelector("[data-clip-flip]");
  const audioBtn = root.querySelector("[data-clip-audio]");
  const fullBtn = root.querySelector("[data-clip-fullscreen]");
  const recordBtn = root.querySelector("[data-clip-record]");
  const stopBtn = root.querySelector("[data-clip-stop]");
  const playback = root.querySelector("[data-clip-playback]");
  const progressEl = root.querySelector("[data-clip-progress]");
  const rerecordBtn = root.querySelector("[data-clip-rerecord]");
  const uploadBtns = Array.from(root.querySelectorAll("[data-clip-upload]"));
  const statusEl = root.querySelector("[data-clip-status]");
  const form = document.getElementById("clip-upload-form");
  const videoField = document.getElementById("clip_video_signed_id");
  const resultForm = document.getElementById("film-result-form");
  const resultWinner = document.getElementById("film_result_winner_id");
  const resultLoser = document.getElementById("film_result_loser_id");
  const resultOccurred = document.getElementById("film_result_occurred_at");
  const statusLine = document.getElementById("film-status-line");
  const inviteSection = document.querySelector("[data-film-invite]");
  const resultSection = document.querySelector("[data-film-result]");
  const resultStatus = document.getElementById("film-result-status");
  const resultButtons = Array.from(document.querySelectorAll("[data-film-outcome]"));

  let stream = null;
  let recorder = null;
  let chunks = [];
  let blob = null;
  let blobUrl = null;
  let startedAt = 0;
  let timerId = null;
  let wakeLock = null;
  let facingMode = "environment";
  let audioEnabled = false;
  let uploading = false;
  let opponentId = initialOpponentId;
  let clipSaved = initialClipSaved;
  let pollTimer = null;
  let finalizeWatchdog = null;
  // Format fallback chain: supported candidates in preference order, then the
  // browser default (mimeIndex past the end). Advanced on each empty capture.
  // A format that produced a real clip is remembered (localStorage) so the
  // next bout starts with the known-good one instead of rediscovering it.
  const MIME_STORE_KEY = "film-mime-v1";
  const mimeCandidates = supportedMimes();
  let mimeIndex = 0;
  try {
    const stored = window.localStorage.getItem(MIME_STORE_KEY);
    const at = mimeCandidates.indexOf(stored);
    if (at >= 0) mimeIndex = at;
  } catch (_e) {
    // private mode etc. — just start from the top of the chain
  }
  const rememberWorkingMime = (mime) => {
    try {
      if (mime) window.localStorage.setItem(MIME_STORE_KEY, mime);
    } catch (_e) {
      // ignore storage failures
    }
  };

  // One-line camera health summary for the empty-capture diagnostic.
  const liveTrackSummary = () => {
    if (!stream) return "no-stream";
    const tracks = typeof stream.getTracks === "function" ? stream.getTracks() : [];
    if (tracks.length === 0) return "no-tracks";
    return tracks.map((t) => `${t.kind}:${t.readyState}${t.muted ? "(muted)" : ""}`).join(",");
  };

  const setStatus = (message, isError = false) => {
    if (!statusEl) return;
    statusEl.textContent = message || "";
    statusEl.classList.toggle("is-error", Boolean(isError));
  };

  const showStep = (name) => {
    Object.entries(steps).forEach(([key, el]) => {
      if (el) el.hidden = key !== name;
    });
  };

  const stopStream = () => {
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
    stream = null;
    if (preview) preview.srcObject = null;
  };

  const startCamera = async () => {
    if (!window.isSecureContext) {
      throw new Error("The camera needs a secure (HTTPS) connection.");
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("This browser cannot access the camera.");
    }

    stopStream();

    let media;
    try {
      media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facingMode } },
        audio: audioEnabled,
      });
    } catch (_error) {
      // Some cameras refuse a specific facing mode; retry with any camera.
      media = await navigator.mediaDevices.getUserMedia({ video: true, audio: audioEnabled });
    }

    stream = media;
    if (preview) {
      preview.srcObject = stream;
      preview.muted = true;
      await preview.play().catch(() => {});
    }
    if (recordBtn) recordBtn.disabled = false;
  };

  const updateAudioLabel = () => {
    if (!audioBtn) return;
    audioBtn.textContent = audioEnabled ? "Sound on" : "Sound off";
    audioBtn.setAttribute("aria-pressed", audioEnabled ? "true" : "false");
  };

  const stopTimer = () => {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  const startTimer = () => {
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      if (timerEl) timerEl.textContent = formatClock(Math.min(elapsed, maxMs));
      if (elapsed >= maxMs) stopRecording();
    };
    tick();
    timerId = setInterval(tick, 250);
  };

  const requestWakeLock = async () => {
    try {
      if ("wakeLock" in navigator && !wakeLock) {
        wakeLock = await navigator.wakeLock.request("screen");
      }
    } catch (_error) {
      // Wake Lock is a nice-to-have; ignore failures.
    }
  };

  const releaseWakeLock = () => {
    if (!wakeLock) return;
    wakeLock.release().catch(() => {});
    wakeLock = null;
  };

  const setRecordingUi = (recording) => {
    if (recordBtn) recordBtn.hidden = recording;
    if (stopBtn) stopBtn.hidden = !recording;
    if (flipBtn) flipBtn.disabled = recording;
    if (audioBtn) audioBtn.disabled = recording;
    if (badge) {
      badge.hidden = !recording;
      badge.classList.toggle("film-rec-live", recording);
    }
  };

  // Fullscreen the recorder card while filming so the bout fills the screen.
  // Best-effort: iPhone Safari has no element fullscreen, so fall back to the
  // native video player for the preview.
  const enterFilmFullscreen = () => {
    try {
      if (document.fullscreenElement) return;
      if (typeof root.requestFullscreen === "function") {
        const p = root.requestFullscreen();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else if (preview && typeof preview.webkitEnterFullscreen === "function") {
        preview.webkitEnterFullscreen();
      }
    } catch (_e) {
      // Fullscreen is a nice-to-have; recording works without it.
    }
  };

  const exitFilmFullscreen = () => {
    try {
      if (document.fullscreenElement && typeof document.exitFullscreen === "function") {
        const p = document.exitFullscreen();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    } catch (_e) {
      // ignore
    }
  };

  const toggleFilmFullscreen = () => {
    if (document.fullscreenElement) exitFilmFullscreen();
    else enterFilmFullscreen();
  };

    // Always land on the review step after stopping, even if the recorder
  // errored or produced no data — the user must never be stuck with a dead
  // timer and no upload button. An empty capture advances to the next format
  // candidate so re-recording automatically tries something the device can
  // actually encode.
  const finalizeRecording = () => {
    if (finalizeWatchdog) {
      clearTimeout(finalizeWatchdog);
      finalizeWatchdog = null;
    }
    try {
      const usedMime = (recorder && recorder.mimeType) || "";
      const mimeType = normalizeMimeType(usedMime || mimeCandidates[mimeIndex] || "video/webm");
      blob = new Blob(chunks, { type: mimeType });
      const gotChunks = chunks.length;
      chunks = [];

      if (blob.size === 0) {
        const triedDefault = mimeIndex >= mimeCandidates.length;
        const diag = `no data (format: ${usedMime || "default"}, chunks: ${gotChunks}, tracks: ${liveTrackSummary()})`;
        if (!triedDefault) {
          mimeIndex += 1;
          setStatus(`No video data was captured — ${diag}. Switched format; please record again.`, true);
        } else {
          setStatus(`No video data was captured — ${diag}. Try another browser (Chrome works best).`, true);
        }
        uploadBtns.forEach((btn) => (btn.disabled = true));
      } else {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        blobUrl = URL.createObjectURL(blob);
        if (playback) playback.src = blobUrl;
        uploadBtns.forEach((btn) => (btn.disabled = false));
        rememberWorkingMime(usedMime || mimeCandidates[mimeIndex] || "");
        setStatus("Check the clip. Re-record if you need to, or upload it to the session.");
      }
    } catch (error) {
      setStatus(error.message || "Could not finish the recording. Please try again.", true);
    } finally {
      stopStream();
      exitFilmFullscreen();
      setRecordingUi(false);
      showStep("review");
    }
  };

  const startRecording = () => {
    if (!stream || typeof window.MediaRecorder === "undefined") {
      setStatus("Recording is not supported in this browser.", true);
      return;
    }

    chunks = [];
    blob = null;
    const mimeType = mimeCandidates[mimeIndex] || "";
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (_error) {
      recorder = new MediaRecorder(stream);
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = finalizeRecording;

    try {
      recorder.start(RECORD_TIMESLICE_MS);
    } catch (error) {
      // Runtime rejection of the chosen format (isTypeSupported can lie):
      // advance the chain so the next attempt tries something else.
      mimeIndex += 1;
      setStatus(`Could not start recording (${error.message || "format rejected"}). Switched format — try again.`, true);
      return;
    }
    startedAt = Date.now();
    if (timerEl) timerEl.textContent = "0:00";
    startTimer();
    requestWakeLock();
    setRecordingUi(true);
    enterFilmFullscreen();
    setStatus("Recording. Keep the phone steady and the whole bout in frame.");
  };

  function stopRecording() {
    if (!recorder) {
      // No recorder (e.g. start failed) but UI thinks we are recording:
      // still land on review so the user is never stuck.
      stopTimer();
      releaseWakeLock();
      setRecordingUi(false);
      showStep("review");
      return;
    }
    if (recorder.state === "inactive") {
      // onstop already fired or never started — ensure we still show review
      // if there is anything to show.
      stopTimer();
      releaseWakeLock();
      if (chunks.length > 0 || blob) finalizeRecording();
      return;
    }
    stopTimer();
    releaseWakeLock();
    try {
      // Flush any buffered final chunk before stopping; some browsers hold
      // the last second of data until requestData is called.
      try {
        if (typeof recorder.requestData === "function") recorder.requestData();
      } catch (_flushError) {
        // ignore flush failures; stop() still delivers pending data
      }
      recorder.stop();
    } catch (error) {
      setStatus(error.message || "Could not stop the recording.", true);
      finalizeRecording();
      return;
    }
    // Watchdog: if onstop never fires (browser quirk), force the review step.
    if (finalizeWatchdog) clearTimeout(finalizeWatchdog);
    finalizeWatchdog = setTimeout(() => {
      finalizeWatchdog = null;
      if (steps.review && steps.review.hidden) finalizeRecording();
    }, 2000);
  }

  const restartCamera = async () => {
    setStatus("Starting camera...");
    try {
      await startCamera();
      setStatus("");
      showStep("record");
    } catch (error) {
      setStatus(error.message || "Unable to start the camera.", true);
    }
  };

  // Result rides inside the clip POST (nested qr_report fields), so one tap
  // uploads and states the winner — no second form to submit.
  const submitResultOnly = (outcome) => {
    const ids = outcomeIdsFor(outcome);
    if (!ids || !resultForm) {
      if (resultStatus) resultStatus.textContent = "Waiting for your opponent to join before recording the result.";
      return;
    }
    resultWinner.value = String(ids.winnerId);
    resultLoser.value = String(ids.loserId);
    resultOccurred.value = new Date().toISOString();
    if (resultStatus) resultStatus.textContent = "Submitting result…";
    resultForm.requestSubmit();
  };

  // Clip upload only. The result is confirmed separately afterwards in the
  // Who won step below, once the clip is saved and both have joined.
  const saveClip = async () => {
    if (!blob || uploading) return;
    if (!form || !videoField) {
      setStatus("Upload is unavailable on this page. Please reload and try again.", true);
      return;
    }

    uploading = true;
    uploadBtns.forEach((btn) => (btn.disabled = true));
    if (rerecordBtn) rerecordBtn.disabled = true;
    if (progressEl) {
      progressEl.hidden = false;
      progressEl.value = 0;
    }
    setStatus("Uploading clip...");

    try {
      const file = new File(
        [blob],
        `bout-${sessionId}-${Date.now()}.${extensionFor(blob.type)}`,
        { type: blob.type }
      );
      const signedId = await directUpload(file, uploadUrl, (percent) => {
        if (progressEl) progressEl.value = percent;
        setStatus(`Uploading clip... ${percent}%`);
      });

      videoField.value = signedId;

      clipSaved = true;
      setStatus("Saving clip to the session...");
      form.requestSubmit();
    } catch (error) {
      uploading = false;
      uploadBtns.forEach((btn) => (btn.disabled = false));
      if (rerecordBtn) rerecordBtn.disabled = false;
      if (progressEl) progressEl.hidden = true;
      setStatus(error.message || "The upload failed. Please try again.", true);
    }
  };

  const outcomeIdsFor = (outcome) => {
    if (outcome !== "won" && outcome !== "lost") return null;
    if (!opponentId) return null;
    return outcome === "won"
      ? { winnerId: myWrestlerId, loserId: opponentId }
      : { winnerId: opponentId, loserId: myWrestlerId };
  };

  // Who won appears below the recorder only once the clip is saved AND the
  // opponent has joined. Late joins swap it in without a refresh.
  const revealOutcomeUi = () => {
    if (!opponentId) return;
    if (inviteSection) inviteSection.hidden = true;
    if (!clipSaved) return;
    if (resultSection) resultSection.hidden = false;
  };

  // Live session status polling — reuses the existing JSON endpoint
  const fetchSessionStatus = async () => {
    try {
      // Get sport slug from the page path
      const pathParts = window.location.pathname.split("/").filter(Boolean);
      const sportSlug = pathParts[0] || "sumo";
      const res = await fetch(`/${sportSlug}/qr-sessions/${sessionId}`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const data = await res.json();

      // Update status line
      if (statusLine) {
        if (data.status === "open" || !data.opponentId) {
          statusLine.textContent = "Waiting for your opponent to join\u2026";
        } else if (data.status === "joined" && data.opponentId) {
          const opp = data.participants?.find((p) => p.id !== myWrestlerId);
          const alias = opp?.aliasName || "Opponent";
          // Never build this from a template string: the alias is attacker-
          // controlled. A text node cannot be parsed as markup.
          const strong = document.createElement("strong");
          strong.textContent = alias;
          statusLine.replaceChildren(strong, " is here \u2014 you can film.");
          opponentId = data.opponentId;
          revealOutcomeUi();
        } else if (data.status === "completed") {
          statusLine.textContent = "Bout confirmed. Clip uploaded.";
          if (resultSection) resultSection.hidden = true;
          stopPolling();
        }
      }
    } catch (_) {
      // ignore transient errors while polling
    }
  };

  const startPolling = () => {
    if (pollTimer) return;
    fetchSessionStatus();
    pollTimer = setInterval(fetchSessionStatus, 2000);
  };

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  // "Who won?" buttons on the status section: submit the result right away,
  // same contract as the dashboard outcome buttons.
  resultButtons.forEach((btn) => {
    btn.addEventListener("click", () => submitResultOnly(btn.dataset.filmOutcome));
  });

  // Upload button on the review step
  uploadBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      saveClip();
    });
  });

  const teardown = () => {
    stopTimer();
    stopPolling();
    releaseWakeLock();
    if (finalizeWatchdog) {
      clearTimeout(finalizeWatchdog);
      finalizeWatchdog = null;
    }
    try {
      if (recorder && recorder.state === "recording") recorder.stop();
    } catch (_error) {
      // ignore
    }
    stopStream();
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      blobUrl = null;
    }
    root.dataset.clipRecorderBound = "";
  };
  activeTeardown = teardown;

  if (startBtn) {
    startBtn.addEventListener("click", () => {
      if (attestationBox && !attestationBox.checked) {
        setStatus("Please confirm you are 18 or over and hold the rights to this video.", true);
        attestationBox.focus();
        return;
      }
      restartCamera();
    });
  }

  if (flipBtn) {
    flipBtn.addEventListener("click", () => {
      facingMode = facingMode === "environment" ? "user" : "environment";
      restartCamera();
    });
  }

  if (audioBtn) {
    audioBtn.addEventListener("click", () => {
      audioEnabled = !audioEnabled;
      updateAudioLabel();
      if (stream) restartCamera();
    });
  }

  if (fullBtn) {
    fullBtn.addEventListener("click", toggleFilmFullscreen);
  }

  if (recordBtn) recordBtn.addEventListener("click", startRecording);
  if (stopBtn) stopBtn.addEventListener("click", stopRecording);

  if (rerecordBtn) {
    rerecordBtn.addEventListener("click", () => {
      if (uploading) return;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        blobUrl = null;
      }
      blob = null;
      uploadBtns.forEach((btn) => (btn.disabled = false));
      restartCamera();
    });
  }

  updateAudioLabel();

  // Start polling for live status updates. Server render already carries the
  // opponent id and clip state, so Who won shows immediately when both hold.
  revealOutcomeUi();
  startPolling();

  if (typeof window.MediaRecorder === "undefined" && recordBtn) {
    recordBtn.disabled = true;
    setStatus("Recording is not supported in this browser.", true);
  }
};

document.addEventListener("DOMContentLoaded", initClipRecorder);
document.addEventListener("turbo:load", initClipRecorder);
document.addEventListener("turbo:before-cache", () => activeTeardown && activeTeardown());
window.addEventListener("pagehide", () => activeTeardown && activeTeardown());